import React, { useState } from 'react';
import * as navigate from 'app/area/shared/navigation';
import { MeatballMenu, ButtonCallToAction, ButtonTertiary, PromptConfirmationModal } from 'ui';
import { Tag } from 'app/components/tag';
import { isDisabled } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { getBackRoute } from '../route';
import { ExternalUrlGroupsActionType, ExternalUrlGroupStickyHeaderProps } from '../types';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { LinksGroupComponent } from 'app/area/links-section/links-group';
import { ModalSize } from 'app/components/constants';
import styles from './external-url-group-sticky-header.scss';

export const ExternalUrlGroupStickyHeader: React.FC<ExternalUrlGroupStickyHeaderProps> = ({
  actionType, shouldPrompt, externalUrlGroupItem, name, description, longDescription, externalUrls,
  fetching, translation, formValidation, onValidate, onSave, onDelete
}) => {

  const [isLinksSaveOpen, setIsLinksSaveOpen] = useState(false);
  const [isLinksPreviewOpen, setIsLinksPreviewOpen] = useState(false);

  const openLinksSaveHandler = () => {
    onValidate();

    if (!Object.keys(formValidation).length) {
      setIsLinksSaveOpen(true);
    }
  };

  const closeLinksPreviewHandler = () => {
    setIsLinksSaveOpen(false);
  };

  const openLinksPreviewHandler = () => {
    openLinksPreview();
  };

  const openLinksPreview = () => {
    setIsLinksPreviewOpen(true);
  };

  const closeLinksSaveHandler = () => {
    setIsLinksPreviewOpen(false);
  };

  const updatingItem = isDefinedIncludingZero(externalUrlGroupItem?.id);
  const onCancel = () => {
    navigate.to({ pathname: getBackRoute() });
  };

  const linksGroup = {
    description,
    longDescription,
    links: externalUrls.map(item => ({
      url: item.urlTemplate,
      description: item.code.description,
      longDescription: item.code?.longDescription,
      disabled: item?.disabled
    }))?.filter(item => !item?.disabled)
  };

  const linksPopup = (previewOnly: boolean) =>
    <ModalWrapper
      headerTitle={!previewOnly && translation.external_url_preview_title}
      headerInfo={!previewOnly
        && replaceTemplateString(translation.external_url_preview_subtitle, name)}
      btnTitle={!previewOnly && translation.save}
      onDone={!previewOnly && onSave}
      btnCancelTitle={!previewOnly ? translation.go_back : translation.preview_close}
      onClose={!previewOnly ? closeLinksPreviewHandler : closeLinksSaveHandler}
      size={ModalSize.large}>
      <LinksGroupComponent size="col-12" linksGroup={linksGroup} />
    </ModalWrapper>;

  return <>
    <StickyHeader
      className={styles.header}
      rightSideContent={<>
        <StickyHeaderButtonContainer>
          <ButtonTertiary
            id="external-url-form-sticky-header-cancel-button"
            onClick={onCancel}>
            {translation.cancel}
          </ButtonTertiary>
          <ButtonCallToAction
            id="external-url-form-sticky-header-save-button"
            onClick={openLinksSaveHandler}
            disabled={!shouldPrompt || !description}>
            {translation.save}
          </ButtonCallToAction>
        </StickyHeaderButtonContainer>
        {actionType === ExternalUrlGroupsActionType.edit && externalUrlGroupItem.id !== 0
          ? <MeatballMenu id="external-url-form-sticky-header-meatballmenu">
            {!!description && <li>
              <button className="dropdown-item"
                onClick={openLinksPreviewHandler}>
                {translation.preview_title}
              </button>
            </li>}
            <li>
              <PromptConfirmationModal
                title={translation.delete_external_url_group}
                description={replaceTemplateString(
                  translation.delete_confirmation,
                  name)}
                continueText={translation.delete}
                cancelText={translation.cancel}
                onContinue={onDelete}>
                <button type="button" className="dropdown-item" disabled={fetching}>
                  {translation.delete}
                </button>
              </PromptConfirmationModal>
            </li>
          </MeatballMenu> : null}
      </>} >
      <div className={styles.headerContainer}>
        {updatingItem ? <span className={styles.headerId}>
          #{externalUrlGroupItem.id}
        </span> : null}
        <h2 className={styles.headerTitle}>
          {updatingItem
            ? name || null
            : translation.new_external_url_group}
        </h2>
        {isDisabled(externalUrlGroupItem.disabled) ? <Tag>{translation.disabled}</Tag> : null}
      </div>
    </StickyHeader>

    {isLinksSaveOpen && linksPopup(false)}
    {isLinksPreviewOpen && linksPopup(true)}
  </>;
};
